import React, { useState } from 'react';
import { photoIdApi } from '../api/photoId';

/**
 * Admin-only undo for a staff manual ID upload made in error (wrong file
 * attached / wrongly answered "verified") - the incident of 21 Aug 2026.
 *
 * Renders a small destructive button; confirming calls the reset endpoint
 * and refreshes the Photo ID panel via onReset. The caller gates visibility
 * (admin + status approved + method 'manual').
 *
 * The confirm dialog is styled entirely inline ON PURPOSE: this app has at
 * least ten competing global `.modal-body` definitions (CorrespondenceViewer's
 * sets display:flex, which lays a modal's children out in a row), so shared
 * modal classes are not safe to build on here.
 *
 * The backend route sends NO email - deliberately not the reject flow, which
 * notifies the client.
 *
 * wholePerson: this record's ID check was copied from another record for the
 * same person, so the backend resets the person (every record) - the button
 * and dialog say so.
 */
const ResetIdVerificationButton: React.FC<{
  clientId: string;
  onReset: () => void | Promise<void>;
  wholePerson?: boolean;
}> = ({ clientId, onReset, wholePerson = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    if (submitting) return;
    setShowModal(false);
    setError(null);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await photoIdApi.resetManualVerification(clientId);
      setShowModal(false);
      await onReset();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to reset ID verification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Placement is the parent's job - this sits inline in the section header */}
      <button
        onClick={() => setShowModal(true)}
        style={{
          padding: '9px 15px',
          backgroundColor: '#d32f2f',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '13px'
        }}
      >
        {wholePerson ? 'Reset ID for this person' : 'Reset ID'}
      </button>

      {showModal && (
        <div
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 12000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '10px',
              width: '440px',
              maxWidth: '92vw',
              boxShadow: '0 12px 40px rgba(15, 23, 42, 0.25)',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #ececec',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '17px', color: '#1a1a1a' }}>
                {wholePerson ? "Reset this person's ID check?" : 'Reset ID verification?'}
              </h3>
              <button
                onClick={close}
                aria-label="Close"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  color: '#888',
                  cursor: 'pointer',
                  lineHeight: 1,
                  padding: '2px 6px'
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: '16px 20px', fontSize: '14px', lineHeight: 1.6, color: '#333' }}>
              {wholePerson && (
                <p style={{ margin: '0 0 10px' }}>
                  Their ID was checked on another of their records. Resetting it here resets it on all their records.
                </p>
              )}
              <p style={{ margin: '0 0 10px' }}>This undoes the staff upload:</p>
              <div
                style={{
                  background: '#f8f9fa',
                  border: '1px solid #ececec',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  margin: '0 0 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '9px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '15px', lineHeight: '20px' }}>↩️</span>
                  {/* explicit black bold - a global rule colours <strong> blue here */}
                  <span>The client returns to <span style={{ fontWeight: 700, color: '#000' }}>not verified</span></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '15px', lineHeight: '20px' }}>🗑️</span>
                  <span>The uploaded document is <span style={{ fontWeight: 700, color: '#000' }}>permanently deleted</span></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '15px', lineHeight: '20px' }}>⏰</span>
                  <span>Automatic ID reminders resume</span>
                </div>
              </div>
              <p style={{ margin: 0, color: '#777', fontSize: '13px' }}>
                No email is sent. The reset is recorded in the audit trail.
              </p>
              {error && (
                <div style={{ color: '#d32f2f', fontSize: '13px', marginTop: '10px' }}>{error}</div>
              )}
            </div>

            <div
              style={{
                padding: '14px 20px',
                borderTop: '1px solid #ececec',
                background: '#fafafa',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}
            >
              <button
                onClick={close}
                disabled={submitting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {submitting ? 'Resetting…' : 'Yes, reset it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResetIdVerificationButton;
